import { Controller } from "@hotwired/stimulus"

// Category customization editor (categories/edit) + categories index search.
//
// Edit page: a preview pill at the top shows the FINAL result — selected
// glyph, selected color, typed name — updating live. The icon grid itself
// keeps default colors; only the preview repaints when a color is picked.
// Index page: uses #filter and #showKind (Needs | Wants tabs) only.
export default class extends Controller {
  static targets = ["iconField", "colorField", "iconItem", "swatch", "previewTile", "previewLabel", "stage", "kindTabs", "emptyState"]

  connect() {
    // The categories index uses this controller only for #filter / #showKind.
    if (!this.hasColorFieldTarget) return

    // Re-apply persisted color to the preview so it opens accurate.
    const key = this.colorFieldTarget.value
    if (key) {
      const swatch = this.swatchTargets.find(s => s.dataset.colorKey === key)
      if (swatch) this.#applyColor(swatch)
    }
  }

  selectIcon(event) {
    const btn = event.currentTarget
    this.iconFieldTarget.value = btn.dataset.cat
    this.iconFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))

    this.iconItemTargets.forEach(item => {
      const halo = item.querySelector(".expense-icon-halo")
      if (halo) halo.classList.toggle("is-selected", item === btn)
    })

    // Mirror the picked glyph into the preview (svg only — grid tiles sit on
    // a neutral background, so their colors must not leak into the preview).
    // The glyph's own default colors come from the button's data attributes
    // and apply when no palette color is selected.
    const gridSvg = btn.querySelector(".expense-icon-tile svg")
    const prevSvg = this.previewTileTarget.querySelector("svg")
    if (gridSvg && prevSvg) {
      const clone = gridSvg.cloneNode(true)
      clone.setAttribute("width", prevSvg.getAttribute("width"))
      clone.setAttribute("height", prevSvg.getAttribute("height"))
      prevSvg.replaceWith(clone)
    }
    this.previewTileTarget.dataset.colorLight = btn.dataset.colorLight
    this.previewTileTarget.dataset.colorDark = btn.dataset.colorDark
    this.#repaintPreview()
  }

  selectColor(event) {
    const swatch = event.currentTarget
    this.colorFieldTarget.value = swatch.dataset.colorKey
    this.colorFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.#applyColor(swatch)
  }

  // Live name preview — falls back to the default category name when cleared.
  updateName(event) {
    this.previewLabelTarget.textContent =
      event.target.value.trim() || this.previewLabelTarget.dataset.defaultLabel
  }

  // Same behavior as expense-edit#filterCategories: hide non-matching tiles,
  // collapse groups that end up empty. (Used on the categories index.)
  filter(event) {
    this.query = (event.target.value || "").trim().toLowerCase()
    this.#applyVisibility({ replay: false })
  }

  // Index All | Needs | Wants tabs. Tiles carry data-kind; income codes have
  // none and stay visible on every tab.
  showKind(event) {
    const kind = event.currentTarget.dataset.kind
    if (!this.hasKindTabsTarget || this.kindTabsTarget.dataset.kind === kind) return

    this.kindTabsTarget.dataset.kind = kind
    const count = event.currentTarget.querySelector(".category-kind-count")
    if (count) this.#bump(count)
    this.#applyVisibility({ replay: true })
  }

  #applyVisibility({ replay }) {
    const q = this.query || ""
    const kind = this.hasKindTabsTarget ? this.kindTabsTarget.dataset.kind : null
    const matches = { all: 0, need: 0, want: 0 }
    let shown = 0

    this.element.querySelectorAll("[data-cat]").forEach(btn => {
      const label = (btn.dataset.label || btn.textContent || "").toLowerCase()
      const kindOk = !kind || kind === "all" || !btn.dataset.kind || btn.dataset.kind === kind
      const matched = q.length === 0 || label.includes(q)
      if (matched) matches.all++
      if (matched && btn.dataset.kind in matches) matches[btn.dataset.kind]++
      const visible = kindOk && matched
      btn.classList.toggle("hidden", !visible)

      // Replay the staggered entrance on a tab switch, re-indexed so the
      // new side starts from the top instead of its server-side position.
      if (replay && visible && btn.classList.contains("category-cell-in")) {
        btn.style.animationDelay = `${Math.min(shown, 24) * 14}ms`
        btn.style.animation = "none"
        void btn.offsetWidth
        btn.style.animation = ""
      }
      if (visible) shown++
    })

    // Tab counts follow the search; the empty state covers a side with no hits.
    if (this.hasKindTabsTarget) {
      this.kindTabsTarget.querySelectorAll("button[data-kind]").forEach(tab => {
        const count = tab.querySelector(".category-kind-count")
        const next = String(matches[tab.dataset.kind] ?? 0)
        if (count && count.textContent.trim() !== next) {
          count.textContent = next
          this.#bump(count)
        }
      })
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", shown > 0)
      this.emptyStateTarget.classList.toggle("flex", shown === 0)
    }

    this.element.querySelectorAll(".grid").forEach(grid => {
      if (!grid.querySelector("[data-cat]")) return
      const anyVisible = Array.from(grid.children).some(c => !c.classList.contains("hidden"))
      grid.classList.toggle("hidden", !anyVisible)
      const header = grid.previousElementSibling
      if (header) header.classList.toggle("hidden", !anyVisible)

      // Group count chip (index only) tracks the tiles still visible.
      const count = header?.querySelector("[data-group-count]")
      if (count) {
        const visible = Array.from(grid.children).filter(c => !c.classList.contains("hidden")).length
        if (count.textContent.trim() !== String(visible)) {
          count.textContent = visible
          this.#bump(count)
        }
      }
    })
  }

  #bump(el) {
    el.classList.remove("is-bumped")
    void el.offsetWidth
    el.classList.add("is-bumped")
  }

  #applyColor(swatch) {
    this.swatchTargets.forEach(s => {
      s.classList.toggle("ring-2", s === swatch)
      s.classList.toggle("ring-blue-500", s === swatch)
      s.classList.toggle("scale-110", s === swatch)
    })
    this.#repaintPreview()
  }

  // Repaint the preview tile AND the stage tint with the selected palette
  // color, or the current glyph's own defaults when no color is picked —
  // exactly how the app renders the saved customization.
  #repaintPreview() {
    const key = this.colorFieldTarget.value
    const swatch = key && this.swatchTargets.find(s => s.dataset.colorKey === key)
    const light = swatch ? swatch.dataset.colorLight : this.previewTileTarget.dataset.colorLight
    const dark = swatch ? swatch.dataset.colorDark : this.previewTileTarget.dataset.colorDark
    if (!light) return

    this.previewTileTarget.querySelectorAll(".expense-icon-tile, .expense-icon-halo").forEach(el => {
      el.style.setProperty("--c-light", light)
      el.style.setProperty("--c-dark", dark)
    })
    if (this.hasStageTarget) {
      this.stageTarget.style.setProperty("--sc-light", light)
      this.stageTarget.style.setProperty("--sc-dark", dark)
    }

    // The selected grid glyph wears the static selection blue; every other
    // one goes back to the neutral tile so only one icon reads as chosen.
    const selected = this.iconFieldTarget.value
    this.iconItemTargets.forEach(item => {
      const on = item.dataset.cat === selected
      item.querySelectorAll(".expense-icon-tile, .expense-icon-halo").forEach(el => {
        el.style.setProperty("--c-light", on ? item.dataset.selectedLight : item.dataset.neutralLight)
        el.style.setProperty("--c-dark", on ? item.dataset.selectedDark : item.dataset.neutralDark)
      })
    })
  }
}
